import { FormEvent, useState } from "react"
import { Head, useForm, usePage, router } from "@inertiajs/react"
import { Copy, Check, Eye, EyeOff, KeyRound, Ban, Trash2 } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useFormatDateTime } from "@/lib/datetime"
import { SettingsSubNav } from "../SettingsSubNav"

import type { PageProps } from "@/types/inertia"

type ApiTokenRow = {
  id: number
  name: string
  plain_token: string | null
  last_used_at: string | null
  revoked_at: string | null
  created_at: string
}

type Props = {
  api_tokens: ApiTokenRow[]
}

export default function SettingsApiTokensIndex({ api_tokens }: Props) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}

  const form = useForm({ api_token: { name: "" } })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.post("/settings/api_tokens", {
      preserveScroll: true,
      onSuccess: () => form.reset(),
    })
  }

  const revoke = (token: ApiTokenRow) => {
    if (
      window.confirm(
        `Revoke token "${token.name}"? Any client using it will start getting 401 errors immediately. The token row stays for audit.`
      )
    ) {
      router.post(`/settings/api_tokens/${token.id}/revoke`, {}, { preserveScroll: true })
    }
  }

  const destroy = (token: ApiTokenRow) => {
    if (
      window.confirm(
        `Permanently delete token "${token.name}"? This removes the row entirely. Any client using it will get 401s. This can't be undone.`
      )
    ) {
      router.delete(`/settings/api_tokens/${token.id}`, { preserveScroll: true })
    }
  }

  return (
    <>
      <Head title="API tokens">
        <meta
          name="description"
          content="Mint, view, revoke, and delete bearer tokens for SparkDrop's HTTP API."
        />
        <meta property="og:title" content="API tokens" />
        <meta
          property="og:description"
          content="Mint, view, revoke, and delete bearer tokens for SparkDrop's HTTP API."
        />
      </Head>
      <AppShell>
        <PageHeader
          title="Settings"
          description="Configure how SparkDrop talks to the world."
          tabs={<SettingsSubNav active="api_tokens" />}
        />

        <section className="mt-8">
          <h2 className="text-sm font-semibold">API tokens</h2>
          <p className="mt-1 text-sm text-ink-muted">
            Bearer tokens that authenticate every call to <code>/api/v1/*</code>.
            Reveal and copy the plain value any time. Revoke disables the token
            but keeps the audit row; delete removes it entirely.
          </p>
        </section>

        <section className="mt-8 max-w-md">
          <h3 className="text-sm font-semibold">Mint a new token</h3>
          <form onSubmit={submit} className="mt-3 space-y-3">
            <div className="space-y-2">
              <label htmlFor="api_token_name">Name</label>
              <Input
                id="api_token_name"
                placeholder="e.g. tubery, publishing-agent"
                required
                aria-invalid={!!errors.name}
                value={form.data.api_token.name}
                onChange={(e) =>
                  form.setData("api_token", { name: e.target.value })
                }
              />
              {errors.name && (
                <p className="text-xs text-danger-display">{errors.name}</p>
              )}
            </div>
            <Button type="submit" disabled={form.processing}>
              Create token
            </Button>
          </form>
        </section>

        <section className="mt-12">
          <h3 className="text-sm font-semibold">Existing tokens</h3>
          {api_tokens.length === 0 ? (
            <p className="mt-3 text-sm text-ink-muted">No tokens yet.</p>
          ) : (
            <ul className="mt-3 divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
              {api_tokens.map((token) => (
                <TokenRow
                  key={token.id}
                  token={token}
                  onRevoke={() => revoke(token)}
                  onDelete={() => destroy(token)}
                />
              ))}
            </ul>
          )}
        </section>
      </AppShell>
    </>
  )
}

function TokenRow({
  token,
  onRevoke,
  onDelete,
}: {
  token: ApiTokenRow
  onRevoke: () => void
  onDelete: () => void
}) {
  const fmt = useFormatDateTime()
  const [revealed, setRevealed] = useState(false)
  const [copied, setCopied] = useState(false)

  const masked = "•".repeat(32)

  const copy = async () => {
    if (!token.plain_token) return
    try {
      await navigator.clipboard.writeText(token.plain_token)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // clipboard API can fail in older contexts; user can copy by hand.
    }
  }

  return (
    <li className="flex flex-col gap-3 px-4 py-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm">
            <KeyRound className="h-4 w-4 text-ink-muted" />
            <span className="truncate font-medium text-ink-display">
              {token.name}
            </span>
            {token.revoked_at && <Badge tone="muted">Revoked</Badge>}
          </div>
          <div className="mt-1 text-xs text-ink-muted">
            Created {fmt(token.created_at, { year: "numeric" })}
            {token.last_used_at && (
              <> · Last used {fmt(token.last_used_at, { year: "numeric" })}</>
            )}
            {token.revoked_at && (
              <> · Revoked {fmt(token.revoked_at, { year: "numeric" })}</>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 sm:justify-end">
          {!token.revoked_at && (
            <Button variant="secondary" size="sm" onClick={onRevoke}>
              <Ban className="h-3.5 w-3.5" /> Revoke
            </Button>
          )}
          <Button variant="danger" size="sm" onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <code className="flex-1 truncate rounded border border-hairline bg-surface px-3 py-2 font-mono text-xs">
          {token.plain_token
            ? revealed
              ? token.plain_token
              : masked
            : "Plain value not available (created before viewing was supported)"}
        </code>
        {token.plain_token && (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRevealed((v) => !v)}
              aria-label={revealed ? "Hide token" : "Show token"}
            >
              {revealed ? (
                <>
                  <EyeOff className="h-3.5 w-3.5" /> Hide
                </>
              ) : (
                <>
                  <Eye className="h-3.5 w-3.5" /> Show
                </>
              )}
            </Button>
            <Button variant="secondary" size="sm" onClick={copy}>
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5" /> Copied
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" /> Copy
                </>
              )}
            </Button>
          </>
        )}
      </div>
    </li>
  )
}
